<script lang="ts">
	import type { AnalysisResult } from '#lib/report/engine';
	import type { ExtractedDocument, Issue } from '#lib/types';
	import { t } from '#lib/i18n/state.svelte';
	import { toCsv, toHtml, toJson, downloadFile } from '#lib/report/export';
	import { scoreTone, formatCount } from '#lib/ui/labels';
	import LangToggle from './LangToggle.svelte';
	import OverviewTab from './OverviewTab.svelte';
	import FindingsTab from './FindingsTab.svelte';
	import StatsTab from './StatsTab.svelte';
	import ReferencesTab from './ReferencesTab.svelte';
	import StructureTab from './StructureTab.svelte';
	import DocumentViewer from './DocumentViewer.svelte';
	import Methodology from './Methodology.svelte';

	interface Props {
		analysis: AnalysisResult;
		doc: ExtractedDocument;
		onreset: () => void;
		isDemo?: boolean;
	}

	let { analysis, doc, onreset, isDemo = false }: Props = $props();
	const report = $derived(analysis.report);

	type TabKey = 'overview' | 'findings' | 'stats' | 'references' | 'structure' | 'method';
	let tab = $state<TabKey>('overview');
	let expandedId = $state<string | null>(null);
	let focusId = $state<string | null>(null);
	let viewerOpen = $state(true);

	const tabs = $derived<{ key: TabKey; label: string; count?: number }[]>([
		{ key: 'overview', label: t('report.tab.overview') },
		{ key: 'findings', label: t('report.tab.findings'), count: report.issues.length },
		{ key: 'stats', label: t('report.tab.stats'), count: report.stats.summary.total },
		{ key: 'references', label: t('report.tab.refs'), count: report.citations.summary.citations },
		{
			key: 'structure',
			label: t('report.tab.structure'),
			count: report.structure.summary.tables + report.structure.summary.figures
		},
		{ key: 'method', label: t('report.tab.method') }
	]);

	function jump(issue: Issue) {
		focusId = null;
		viewerOpen = true;
		// Aynı issue'ya tekrar atlanabilsin diye önce temizle, sonra ata
		requestAnimationFrame(() => {
			focusId = issue.id;
		});
	}

	function jumpById(id: string) {
		const issue = report.issues.find((i) => i.id === id);
		if (issue) jump(issue);
	}

	function selectIssue(id: string) {
		expandedId = expandedId === id ? null : id;
		if (expandedId) {
			const el = document.getElementById(`issue-${id}`);
			el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
			tab = 'findings';
		}
	}

	function exportJson() {
		downloadFile(toJson(report), baseName() + '.json', 'application/json');
	}
	function exportHtml() {
		downloadFile(toHtml(report), baseName() + '.html', 'text/html');
	}
	function exportCsv() {
		downloadFile(toCsv(report), baseName() + '.csv', 'text/csv');
	}
	function baseName() {
		return report.document.fileName.replace(/\.[^.]+$/, '') + '-repcheck';
	}

	const qualityLabel = $derived(
		report.document.quality.level === 'high'
			? t('report.qualityHigh')
			: report.document.quality.level === 'medium'
				? t('report.qualityMedium')
				: t('report.qualityLow')
	);
</script>

<div class="report">
	<header class="head">
		<div class="head-left">
			{#if isDemo}
				<button class="btn btn-primary btn-sm back" onclick={onreset}>{t('demo.ownCta')}</button>
			{:else}
				<button class="btn btn-ghost btn-sm back" onclick={onreset}>← {t('report.back')}</button>
			{/if}
			<div class="file-meta">
				<h1>{report.document.fileName}</h1>
				<p class="sub">
					<span class="chip chip-neutral">{report.document.format.toUpperCase()}</span>
					{#if report.document.pages}
						<span>{t('viewer.pages', { n: report.document.pages })}</span>
					{/if}
					<span>{t('report.charsK', { n: Math.round(report.document.chars / 1000) })}</span>
					<span class="dot-sep">·</span>
					<span style:color={qualityLabel === t('report.qualityLow') ? 'var(--critical)' : 'inherit'}>
						{t('report.quality', { level: qualityLabel })}
					</span>
				</p>
			</div>
		</div>

		<div class="head-right">
			<LangToggle compact />
			<div class="mini-score" title={t('overview.eyebrow')}>
				<span class="ms-num" style:color={scoreTone(report.scores.overall)}>{report.scores.overall}</span>
				<span class="ms-grade" style:color={scoreTone(report.scores.overall)}>{report.scores.grade}</span>
			</div>
			<div class="exports">
				<button class="btn btn-sm" onclick={exportHtml}>{t('report.exportHtml')}</button>
				<button class="btn btn-sm" onclick={exportCsv}>CSV</button>
				<button class="btn btn-sm" onclick={exportJson}>JSON</button>
			</div>
		</div>
	</header>

	<nav class="tabs" aria-label={t('report.tabsAria')}>
		{#each tabs as tabItem (tabItem.key)}
			<button
				class="tab"
				class:active={tab === tabItem.key}
				onclick={() => (tab = tabItem.key)}
				aria-current={tab === tabItem.key ? 'page' : undefined}
			>
				{tabItem.label}
				{#if tabItem.count !== undefined}<span class="tab-count">{formatCount(tabItem.count)}</span>{/if}
			</button>
		{/each}
		<button class="tab viewer-toggle" class:active={viewerOpen} onclick={() => (viewerOpen = !viewerOpen)}>
			{viewerOpen ? t('report.hideText') : t('report.showText')}
		</button>
	</nav>

	<div class="body" class:with-viewer={viewerOpen}>
		<main class="main">
			{#if tab === 'overview'}
				<OverviewTab {report} onjump={jumpById} onselect={selectIssue} />
			{:else if tab === 'findings'}
				<FindingsTab {report} onjump={jump} onselect={selectIssue} {expandedId} />
			{:else if tab === 'stats'}
				<StatsTab {report} onjump={jump} />
			{:else if tab === 'references'}
				<ReferencesTab {report} onjump={jump} />
			{:else if tab === 'structure'}
				<StructureTab {report} onjump={jump} />
			{:else}
				<Methodology {report} {doc} />
			{/if}
		</main>

		{#if viewerOpen}
			<aside class="aside" aria-label="Belge metni">
				<DocumentViewer {doc} issues={report.issues} {focusId} />
			</aside>
		{/if}
	</div>
</div>

<style>
	.report {
		display: flex;
		flex-direction: column;
		min-height: 100vh;
	}
	.head {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 1rem;
		padding: 0.9rem clamp(1rem, 3vw, 2rem);
		border-bottom: 1px solid var(--line);
		background: rgb(250 248 244 / 0.92);
		backdrop-filter: blur(8px);
		flex-wrap: wrap;
		position: sticky;
		top: 0;
		z-index: 20;
	}
	.head-left {
		display: flex;
		align-items: center;
		gap: 0.9rem;
		min-width: 0;
	}
	.back {
		flex: none;
	}
	.file-meta {
		min-width: 0;
	}
	h1 {
		font-family: var(--font-display);
		font-size: clamp(1.15rem, 2.2vw, 1.5rem);
		margin: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		max-width: 60ch;
	}
	.sub {
		display: flex;
		align-items: center;
		gap: 0.45rem;
		flex-wrap: wrap;
		margin: 0.15rem 0 0;
		font-size: 0.75rem;
		color: var(--muted);
	}
	.dot-sep {
		color: var(--line-strong);
	}
	.head-right {
		display: flex;
		align-items: center;
		gap: 0.8rem;
	}
	.mini-score {
		display: flex;
		align-items: baseline;
		gap: 0.25rem;
		font-family: var(--font-display);
		line-height: 1;
	}
	.ms-num {
		font-size: 1.7rem;
	}
	.ms-grade {
		font-size: 0.95rem;
		font-weight: 600;
	}
	.exports {
		display: flex;
		gap: 0.35rem;
	}
	.tabs {
		display: flex;
		gap: 0.15rem;
		padding: 0 clamp(1rem, 3vw, 2rem);
		border-bottom: 1px solid var(--line);
		overflow-x: auto;
		background: var(--paper);
	}
	.tab {
		border: 0;
		background: transparent;
		padding: 0.7rem 0.8rem;
		font-size: 0.86rem;
		color: var(--muted);
		cursor: pointer;
		border-bottom: 2px solid transparent;
		white-space: nowrap;
		display: inline-flex;
		align-items: center;
		gap: 0.35rem;
	}
	.tab:hover {
		color: var(--ink);
	}
	.tab.active {
		color: var(--ink);
		font-weight: 600;
		border-bottom-color: var(--ink);
	}
	.tab-count {
		font-size: 0.7rem;
		background: var(--paper-2);
		border: 1px solid var(--line);
		border-radius: 99px;
		padding: 0 0.35rem;
		color: var(--muted);
		font-variant-numeric: tabular-nums;
	}
	.viewer-toggle {
		margin-left: auto;
		font-size: 0.78rem;
	}
	.body {
		display: grid;
		grid-template-columns: 1fr;
		gap: 1.25rem;
		padding: 1.25rem clamp(1rem, 3vw, 2rem) 3rem;
		align-items: start;
	}
	.body.with-viewer {
		grid-template-columns: minmax(0, 1fr) minmax(380px, 45%);
	}
	@media (max-width: 1080px) {
		.body.with-viewer {
			grid-template-columns: 1fr;
		}
	}
	.main {
		min-width: 0;
	}
	.aside {
		position: sticky;
		top: 125px;
		min-width: 0;
		animation: fade-up 0.4s var(--ease) both;
	}
	@media (max-width: 1080px) {
		.aside {
			position: static;
		}
	}

	@media (max-width: 640px) {
		.head {
			padding: 0.65rem 1rem;
			gap: 0.5rem;
		}
		.head-left {
			flex-wrap: wrap;
			gap: 0.3rem 0.6rem;
			width: 100%;
		}
		h1 {
			white-space: normal;
			font-size: 1.02rem;
			max-width: 100%;
		}
		.head-right {
			width: 100%;
			justify-content: space-between;
		}
		.exports {
			gap: 0.25rem;
		}
		.exports .btn {
			padding: 0.3rem 0.55rem;
			font-size: 0.76rem;
		}
		.ms-num {
			font-size: 1.4rem;
		}
		.tabs {
			position: static;
		}
	}
</style>
